import React, { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, IconFormIcons, Target } from 'shared/assets/icons';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { Icon } from 'shared/components/ui/icon';
import { useTaskCounts } from 'shared/contexts/task-counts/use-task-counts';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { Paths } from 'shared/routes/paths';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { getCompletionPercentage } from 'shared/utils/get-completion-percentage';
import { getGoalCompletion } from 'shared/utils/get-goal-completion';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';

import * as Styled from './search-goal-card.style';

export type SearchGoalCardProps = {
  goal: Goal;
  onClick?: (id: ID) => void;
  tabbable?: boolean;
  goals: Goal[];
  lifeAreas: LifeArea[];
  navTo?: string;
};

export const SearchGoalCard: React.FC<SearchGoalCardProps> = ({
  goal,
  onClick,
  tabbable,
  goals,
  lifeAreas,
  navTo,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const _onClick = (e: React.MouseEvent | KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    navigate(navTo ?? Paths.GoalDetail.replace(':id', goal.id));
    onClick?.(goal.id);
  };

  useKeysDown(
    ['Enter'],
    (e) => {
      if (tabbable && document.activeElement === containerRef.current) {
        _onClick(e);
      }
    },
    { otherKeysInCombinationAllowed: true },
  );

  const taskCounts = useTaskCounts();

  const { end, percentage } = useMemo(() => {
    const { start, current, end } = getGoalCompletion(goal, {
      goals,
      goalProgressCounts: taskCounts?.byGoalProgress,
    });
    const percentage = getCompletionPercentage({ start, current, end });

    return {
      end,
      percentage,
    };
  }, [goal, goals, taskCounts?.byGoalProgress]);

  const color = getLinkedItemColor(goal.id, { goals, lifeAreas });

  return (
    <Styled.Container
      ref={containerRef}
      onClick={_onClick}
      $isClickable
      tabIndex={tabbable ? 0 : undefined}
    >
      <Styled.IconContainer $color={color}>
        {end === Infinity || !!goal.completedAt ? (
          <Icon icon={goal.iconName ? IconFormIcons[goal.iconName] : Target} />
        ) : (
          <CircleProgress percentage={percentage} hidePercentage isSmall />
        )}

        {!!goal.completedAt && (
          <Styled.CompletedContainer>
            <Icon icon={Check} />
          </Styled.CompletedContainer>
        )}
      </Styled.IconContainer>

      <Styled.Title $isCompleted={!!goal.completedAt}>{goal.name}</Styled.Title>
    </Styled.Container>
  );
};
